/**
 * ★ A DOT PER CATEGORY - the prototype's legend (`calLegend`, one coloured dot per category over the
 * calendar), on the one category fact this calendar has: `dayLoad[].byCategory`, how many pieces of
 * each category are scheduled across MoonX on a day (counts only, the owner's 2026-08-26 ruling). The
 * owner's live look at real cabinets, 2026-10-01 / design 15.
 *
 * THE COLOURS ARE TOKENS, NOT A CATEGORY PALETTE. The prototype gives each category its own hue; this
 * app has no such token and a screen may not invent a colour (web/ui/eslint.config.mjs, the raw-colour
 * ban). So a category gets the next of ten marks (./calendar.module.css: five token colours, then
 * the same five as rings) in the order the CATALOGUE lists categories, among the categories the
 * month actually has - a month of six categories never repeats a mark. A mark is never the only
 * channel: the legend and the day pane print the category's name beside it, and the day cell's own
 * accessible sentence says the count in words.
 *
 * Names come from `../category-labels.ts` - the catalogue's own, and a code it does not hold
 * verbatim, never prettified.
 */
import classes from './calendar.module.css';
import { categoryName } from '../category-labels';
import { useTranslation } from '../../../i18n';

/** The ten marks, in order. Held as a list so an index is never a computed class name. */
const MARKS: readonly (string | undefined)[] = [
  classes.mark0,
  classes.mark1,
  classes.mark2,
  classes.mark3,
  classes.mark4,
  classes.mark5,
  classes.mark6,
  classes.mark7,
  classes.mark8,
  classes.mark9,
];

export interface CategoryMark {
  readonly code: string;
  /** The catalogue's name, or the code verbatim. */
  readonly name: string;
  /** Which of the ten marks it wears. */
  readonly mark: number;
}

/**
 * The categories among `codes`, each with its name and its mark: catalogue order first (the order of
 * `labels`, which is the catalogue's), then any code the catalogue does not hold, in the order it
 * was met. Returned as a map from code, in that order.
 */
export function categoryMarks(
  codes: Iterable<string>,
  labels: ReadonlyMap<string, string>,
): ReadonlyMap<string, CategoryMark> {
  const present = new Set(codes);
  const ordered = [
    ...[...labels.keys()].filter((code) => present.has(code)),
    ...[...present].filter((code) => !labels.has(code)),
  ];
  return new Map(
    ordered.map((code, index) => [
      code,
      { code, name: categoryName(labels, code), mark: index % MARKS.length },
    ]),
  );
}

/** Every category code a run of day loads mentions. */
export function categoriesIn(
  loads: Iterable<{ readonly byCategory?: readonly { readonly category: string }[] }>,
): string[] {
  const codes: string[] = [];
  for (const load of loads) {
    for (const one of load.byCategory ?? []) {
      codes.push(one.category);
    }
  }
  return codes;
}

/** The mark itself. Decoration beside words, so hidden from a screen reader. */
export function CategoryDot({ mark }: { readonly mark: number }) {
  return <span className={`${classes.mark ?? ''} ${MARKS[mark] ?? ''}`} aria-hidden="true" />;
}

/**
 * The legend over the month: each category the month has, its mark and its name. A list, named, so
 * a screen reader hears what the dots on the days stand for.
 */
export function CategoryLegend({ marks }: { readonly marks: ReadonlyMap<string, CategoryMark> }) {
  const { t } = useTranslation('calendar');
  if (marks.size === 0) {
    return null;
  }
  return (
    <ul className={classes.legend} aria-label={t('legend')}>
      {[...marks.values()].map((entry) => (
        <li key={entry.code} className={classes.legendEntry}>
          <CategoryDot mark={entry.mark} />
          {entry.name}
        </li>
      ))}
    </ul>
  );
}
